// Browser-side photo shrink for scan uploads.
//
// Vercel rejects request bodies over 4.5 MB in front of the backend
// (FUNCTION_PAYLOAD_TOO_LARGE), and a modern phone photo alone can exceed
// that. The parse worker downscales every photo to a 2576 px long edge before
// sending it to Claude vision (see backend/app/workers/paper_scan_parse.py),
// so pixels above that limit never survive anyway. Shrinking in the browser
// keeps every upload comfortably under the platform cap at zero quality cost.

const MAX_LONG_EDGE = 2576;
const JPEG_QUALITY = 0.85;
// Below this size an already-small image is passed through untouched.
const PASSTHROUGH_BYTES = 3 * 1024 * 1024;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode failed"));
    img.src = url;
  });
}

/**
 * Downscale a photo to the vision model's 2576 px long-edge limit and
 * re-encode as JPEG. Returns the original file when it is already small
 * enough, or when the browser cannot decode it (the backend then rejects
 * unsupported formats with its own clear message).
 */
export async function downscaleImageForUpload(file: File): Promise<File> {
  const url = URL.createObjectURL(file);
  let img: HTMLImageElement;
  try {
    // <img> decode applies EXIF orientation in all modern browsers, so the
    // re-encoded JPEG comes out upright even though EXIF is stripped.
    img = await loadImage(url);
  } catch {
    URL.revokeObjectURL(url);
    return file;
  }

  try {
    const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
    if (longEdge <= MAX_LONG_EDGE && file.size <= PASSTHROUGH_BYTES) {
      return file;
    }
    const scale = Math.min(1, MAX_LONG_EDGE / longEdge);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
    );
    if (!blob) return file;
    const stem = file.name.replace(/\.[^.]+$/, "") || "mynd";
    return new File([blob], `${stem}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(url);
  }
}
